/**
 * Cajon de filtros — controlador JavaScript
 * Handles: drawer open/close, body scroll lock, filter form submission,
 * keyboard navigation (Escape), focus management, mobile swipe-to-close,
 * iOS scroll bounce prevention, touch optimizations
 */
(function () {
  'use strict';

  /* ---- Selectors ---- */
  var SEL = {
    trigger:  '[data-lej-filter-trigger]',
    drawer:   '[data-lej-drawer]',
    overlay:  '[data-lej-overlay]',
    close:    '[data-lej-close]',
    form:     '[data-lej-filter-form]',
    apply:    '[data-lej-apply]',
    body:     '.lej-drawer__body'
  };

  var scrollY = 0;
  var touchStartX = 0;
  var touchStartY = 0;
  var isSwiping = false;

  /* ==================================================
     DRAWER — Open / Close with scroll-lock & focus
     ================================================== */

  function openDrawer() {
    var drawer  = document.querySelector(SEL.drawer);
    var overlay = document.querySelector(SEL.overlay);
    if (!drawer || !overlay) return;

    /* Save scroll position and lock body (iOS-compatible) */
    scrollY = window.scrollY;
    document.body.style.top = '-' + scrollY + 'px';
    document.body.classList.add('lej-drawer-open');

    /* Show overlay and drawer */
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    /* Focus the close button after transition */
    setTimeout(function () {
      var closeBtn = drawer.querySelector(SEL.close);
      if (closeBtn) closeBtn.focus();
    }, 120);

    /* Listen for Escape */
    document.addEventListener('keydown', onEscape);

    /* Prevent background touchmove on iOS */
    overlay.addEventListener('touchmove', preventScroll, { passive: false });
  }

  function closeDrawer() {
    var drawer  = document.querySelector(SEL.drawer);
    var overlay = document.querySelector(SEL.overlay);
    if (!drawer || !overlay) return;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    /* Restore scroll */
    document.body.style.top = '';
    document.body.classList.remove('lej-drawer-open');
    window.scrollTo(0, scrollY);

    document.removeEventListener('keydown', onEscape);
    overlay.removeEventListener('touchmove', preventScroll);

    /* Return focus to trigger */
    var trigger = document.querySelector(SEL.trigger);
    if (trigger) trigger.focus();
  }

  function onEscape(e) {
    if (e.key === 'Escape') closeDrawer();
  }

  function preventScroll(e) {
    e.preventDefault();
  }

  /* Helper: check if a touch target is inside the price-range slider */
  function isTouchOnPriceRange(target) {
    return target && target.closest && target.closest('.cc-price-range__track-wrapper, .cc-price-range__control, price-range');
  }

  /* ==================================================
     MOBILE — Swipe right to close (like native iOS)
     ================================================== */

  function initSwipeToClose() {
    var drawer = document.querySelector(SEL.drawer);
    if (!drawer) return;

    drawer.addEventListener('touchstart', function (e) {
      /* Don't start swipe if touching price range slider */
      if (isTouchOnPriceRange(e.target)) return;
      var touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      isSwiping = false;
    }, { passive: true });

    drawer.addEventListener('touchmove', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      /* Don't interfere with price range slider drags */
      if (isTouchOnPriceRange(e.target)) return;

      var touch = e.touches[0];
      var deltaX = touch.clientX - touchStartX;
      var deltaY = touch.clientY - touchStartY;

      /* Only swipe if horizontal movement > vertical (not scrolling) */
      if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 30) {
        isSwiping = true;
        /* Visual feedback: translate drawer as user swipes */
        drawer.style.transition = 'none';
        drawer.style.transform = 'translateX(' + Math.max(0, deltaX - 30) + 'px)';
      }
    }, { passive: true });

    drawer.addEventListener('touchend', function (e) {
      if (!isSwiping) return;

      var touch = e.changedTouches[0];
      var deltaX = touch.clientX - touchStartX;

      /* Reset transition */
      drawer.style.transition = '';
      drawer.style.transform = '';

      /* If swiped far enough (>100px), close drawer */
      if (deltaX > 100) {
        closeDrawer();
      }

      isSwiping = false;
    }, { passive: true });
  }

  /* ==================================================
     MOBILE — Prevent body scroll when touching overlay
     but allow scroll inside drawer body
     ================================================== */

  function initTouchScrollControl() {
    var drawerBody = document.querySelector(SEL.body);
    if (!drawerBody) return;

    /* Allow scroll inside the drawer body only */
    drawerBody.addEventListener('touchmove', function (e) {
      /* Don't intercept touch on price range — let it bubble to window */
      if (isTouchOnPriceRange(e.target)) return;

      /* Allow scroll if content is scrollable */
      if (drawerBody.scrollHeight > drawerBody.clientHeight) {
        e.stopPropagation();
      }
    }, { passive: true });
  }

  /* ==================================================
     FILTERS — Serialize form & navigate
     ================================================== */

  function applyFilters() {
    var form = document.querySelector(SEL.form);
    if (!form) return;

    var params = [];

    /* 1) All checked checkboxes (talla, color, material, genero) */
    var checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
    for (var i = 0; i < checkboxes.length; i++) {
      var cb = checkboxes[i];
      if (cb.name) {
        params.push(encodeURIComponent(cb.name) + '=' + encodeURIComponent(cb.value));
      }
    }

    /* 2) Price range — read from price-range.liquid text inputs */
    var priceMin = form.querySelector('.cc-price-range__input--min');
    var priceMax = form.querySelector('.cc-price-range__input--max');

    if (priceMin || priceMax) {
      var minVal = priceMin ? priceMin.value.trim() : '';
      var maxVal = priceMax ? priceMax.value.trim() : '';
      var maxLimit = priceMax ? (priceMax.getAttribute('max') || priceMax.getAttribute('placeholder') || '') : '';

      /* Only send min if user set a value > 0 */
      if (minVal !== '' && parseInt(minVal) > 0 && priceMin.name) {
        params.push(encodeURIComponent(priceMin.name) + '=' + encodeURIComponent(minVal));
      }

      /* Only send max if less than the full range max */
      if (maxVal !== '' && parseInt(maxVal) < parseInt(maxLimit) && priceMax.name) {
        params.push(encodeURIComponent(priceMax.name) + '=' + encodeURIComponent(maxVal));
      }
    }

    /* 3) Preserve current sort_by if present */
    try {
      var currentUrl = new URL(window.location.href);
      var sortBy = currentUrl.searchParams.get('sort_by');
      if (sortBy) {
        params.push('sort_by=' + encodeURIComponent(sortBy));
      }
    } catch (e) { /* ignore */ }

    /* Build URL and navigate */
    var collectionPath = window.location.pathname;
    var qs = params.join('&');
    var newUrl = qs ? collectionPath + '?' + qs : collectionPath;

    window.location.href = newUrl;
  }

  /* ==================================================
     PRICE-RANGE "a" SEPARATOR — inject between inputs
     ================================================== */

  function addPriceSeparator() {
    var rows = document.querySelectorAll('.lej-price-range .cc-price-range__input-row');
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      /* Only add once */
      if (row.querySelector('.lej-price-separator')) continue;

      var containers = row.querySelectorAll('.cc-price-range__input-container');
      if (containers.length === 2) {
        var sep = document.createElement('span');
        sep.className = 'lej-price-separator';
        sep.textContent = 'a';
        row.insertBefore(sep, containers[1]);
      }
    }
  }

  /* ==================================================
     FIX PRICE VALUES — Clamp inflated values from
     Shopify's filter.max_value.value bug
     ================================================== */

  function fixPriceValues() {
    var priceMin = document.querySelector('.lej-price-range .cc-price-range__input--min');
    var priceMax = document.querySelector('.lej-price-range .cc-price-range__input--max');
    if (!priceMin && !priceMax) return;

    var maxLimit = priceMax ? parseInt(priceMax.getAttribute('max') || priceMax.getAttribute('placeholder') || '0') : 0;
    if (maxLimit <= 0) return;

    /* Fix min: if value > maxLimit, it's inflated */
    if (priceMin && priceMin.value) {
      var minVal = parseInt(priceMin.value);
      if (minVal > maxLimit) {
        /* Try dividing by 100 to fix Shopify's scale mismatch */
        var fixed = Math.round(minVal / 100);
        if (fixed <= maxLimit) {
          priceMin.value = fixed;
        } else {
          priceMin.value = '';
        }
      }
    }

    /* Fix max: if value > maxLimit, it's inflated */
    if (priceMax && priceMax.value) {
      var maxVal = parseInt(priceMax.value);
      if (maxVal > maxLimit) {
        var fixedMax = Math.round(maxVal / 100);
        if (fixedMax <= maxLimit) {
          priceMax.value = fixedMax;
        } else {
          priceMax.value = maxLimit;
        }
      }
    }

    /* Trigger the price-range component to re-render the slider position */
    var priceRangeEl = document.querySelector('.lej-price-range price-range');
    if (priceRangeEl && typeof priceRangeEl.updateBar === 'function') {
      priceRangeEl.updateBar();
    } else {
      /* Fallback: dispatch input event to trigger re-render */
      if (priceMin) priceMin.dispatchEvent(new Event('input', { bubbles: true }));
      if (priceMax) priceMax.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /* ==================================================
     INIT — Bind all events
     ================================================== */

  function init() {
    /* Open trigger(s): delegado en document para sobrevivir a los reemplazos
       del DOM por ajax (en /search el botón vive dentro de un data-ajax-container
       y la paginación/orden lo re-pintan sin sus listeners). */
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest ? e.target.closest(SEL.trigger) : null;
      if (!trigger) return;
      e.preventDefault();
      openDrawer();
    });

    /* Close button(s) */
    var closeBtns = document.querySelectorAll(SEL.close);
    for (var c = 0; c < closeBtns.length; c++) {
      closeBtns[c].addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    }

    /* Overlay click → close */
    var overlay = document.querySelector(SEL.overlay);
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    /* Apply button */
    var applyBtn = document.querySelector(SEL.apply);
    if (applyBtn) {
      applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        applyFilters();
      });
    }

    /* Fix inflated price values from Shopify */
    fixPriceValues();

    /* Inject price separator */
    addPriceSeparator();

    /* Mobile: swipe to close + scroll control */
    initSwipeToClose();
    initTouchScrollControl();
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
